import { Injectable } from '@angular/core';
import { environment as env } from '../../../environments/environment';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { StorageService } from './storage.service';
import { HttpService } from './http.service';
import { API_AUTH_LOGOUT, LOGIN_ROUTE } from '../constants/constants';
import { ApiVariablesService } from './api-variables.service';
import { ROLES } from '../constants/role.constants';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  constructor(
    private readonly router: Router,
    private readonly storageService: StorageService,
    private readonly httpService: HttpService,
    private readonly apiService: ApiVariablesService
  ) {}

  login(payload: any): Observable<any> {
    return this.httpService.post<any>(`${env.baseUrl}${this.apiService.getEndpoint("login")}`, payload);
  }

  logout(): void {
    this.storageService.clearStorage();
    this.router.navigate([LOGIN_ROUTE]);
  }

  forgotPassword(email: string): Observable<object> {
    return this.httpService.post(env.baseUrl + '/forgot-password', { email });
  }

  getUserProfile(): Observable<any> {
    return this.httpService.get<any>(env.baseUrl + `/profile`);
  }

  updateProfile(request: any): Observable<any> {
    return this.httpService.post<any>(`${env.baseUrl}${this.apiService.getEndpoint("changePwd")}`, request);
  }

  changePassword(request: any): Observable<object> {
    return this.httpService.put(env.baseUrl + '/update-password', request);
  }

  resetPassword(request: any): Observable<object> {
    return this.httpService.post(env.baseUrl + '/reset-password', request);
  }

  logoutApi(): Observable<object> {
    return this.httpService.post(env.baseUrl + API_AUTH_LOGOUT, {});
  }

  getUser() {
    return this.storageService.getFromLocalOrSessionStorage('user');
  }

  getRole() {
    const user = this.getUser();
    return user ? user.role : null;
  }

  getRoleName(): string | null {
    const role = this.getRole();
    if (!role) return null;
    
    // Handle both string and object role formats
    if (typeof role === 'string') {
      return role;
    } else if (role && typeof role === 'object' && role.name) {
      return role.name;
    }
    
    return null;
  }

  isAdmin(): boolean {
    const roleName = this.getRoleName();
    return roleName?.toLowerCase() === ROLES.ADMIN.toLowerCase();
  }

  isSecretary(): boolean {
    const roleName = this.getRoleName();
    return roleName?.toLowerCase() === ROLES.SECRETARY.toLowerCase();
  }

  hasRole(role: string): boolean {
    const roleName = this.getRoleName();
    return roleName?.toLowerCase() === role.toLowerCase();
  }

  hasAnyRole(roles: string[]): boolean {
    const roleName = this.getRoleName();
    if (!roleName) return false;
    return roles.some(role => roleName.toLowerCase() === role.toLowerCase());
  }

  isAuthenticated(): boolean {
    const user = this.getUser();
    return user !== null && user !== undefined && Object.keys(user).length > 0;
  }
}
